import React, { useMemo } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  castToNamedFilterFragment,
  filterWidgetSchema,
  IFilterWidgetState,
} from './types';
import { MaybeNil } from '../../utils/interfaces';
import {
  FilterType,
  IFilterFieldsFragment,
} from '../../utils/apollo/graphqlSchema';
import * as FilterWidgetUI from './FilterWidgetUI';
import FilterWidgetNamedView from './FilterWidgetNamedView';
import FilterWidgetBasic from './FilterWidgetBasic';
import FilterWidgetSubmit from './FilterWidgetSubmit';
import useDefaultFilterWidgetValues from './utils/useDefaultFilterWidgetValues';
import {
  MAX_FILTER_CONDITIONS,
  MAX_PREDICATES_IN_FILTER_CONDITION,
} from '../../config/constants';
import { assertNotNil } from '../../utils/types';
import {
  FilterWidgetPropContext,
  IFilterWidgetPropValues,
} from './FilterWidgetPropContext';
import { getFilterWidgetOutput } from './utils/getFilterWidgetOutput';
import { useAnalytics } from '../../utils/analytics/useAnalytics';
import { formErrorViewed } from '../../utils/analytics/events/error/events';
import { AnalyticsErrorContext } from '../../utils/analytics/events/error/types';
import { countReactHookFormErrors } from '../Builder/utils/errorUtil';

interface IProps extends IFilterWidgetPropValues {
  onSubmit: (newFilter: IFilterFieldsFragment) => void;
  defaultFilter: MaybeNil<IFilterFieldsFragment>;
  maxConditions?: number;
  maxPredicates?: number;
  includeAnyValue?: boolean;
}

const FilterWidget = ({
  onSubmit,
  schemaId,
  context,
  defaultFilter,
  defaultLogOps,
  lockedField,
  maxConditions = MAX_FILTER_CONDITIONS,
  maxPredicates = MAX_PREDICATES_IN_FILTER_CONDITION,
  disableNamedFilters,
  includeAnyValue = false,
}: IProps) => {
  const { track } = useAnalytics();
  const defaultValues = useDefaultFilterWidgetValues(
    defaultFilter,
    lockedField,
  );

  const formMethods = useForm<IFilterWidgetState>({
    resolver: zodResolver(filterWidgetSchema),
    defaultValues,
  });

  const { control, handleSubmit } = formMethods;

  const formType = useWatch({
    name: 'type',
    control,
  });
  const namedFilter = useWatch({
    name: 'namedFilter',
    control,
  });

  const onSubmitForm = handleSubmit(
    (formData) => {
      if (formData.type === FilterType.Named) {
        assertNotNil(formData.namedFilter);

        onSubmit(castToNamedFilterFragment(formData.namedFilter));
      } else {
        const newBasicFilter = getFilterWidgetOutput(formData);

        onSubmit(newBasicFilter);
      }
    },
    (errors) => {
      track(
        formErrorViewed({
          message: `Failed to confirm filter. ${countReactHookFormErrors(errors)} errors found.`,
          context: AnalyticsErrorContext.FilterWidget,
        }),
      );
    },
  );

  const propContextValue = useMemo(
    () => ({
      schemaId,
      context,
      defaultLogOps,
      lockedField,
      disableNamedFilters,
      includeAnyValue,
    }),
    [
      disableNamedFilters,
      context,
      defaultLogOps,
      lockedField,
      schemaId,
      includeAnyValue,
    ],
  );

  return (
    <FormProvider {...formMethods}>
      <FilterWidgetUI.FormContainer onSubmit={onSubmitForm}>
        {formType === FilterType.Named && namedFilter ? (
          <FilterWidgetNamedView
            namedFilter={castToNamedFilterFragment(namedFilter)}
          />
        ) : (
          <FilterWidgetPropContext.Provider value={propContextValue}>
            <FilterWidgetBasic
              maxConditions={maxConditions}
              maxPredicates={maxPredicates}
            />
          </FilterWidgetPropContext.Provider>
        )}
        <FilterWidgetUI.Footer>
          <FilterWidgetSubmit />
        </FilterWidgetUI.Footer>
      </FilterWidgetUI.FormContainer>
    </FormProvider>
  );
};

export default FilterWidget;
